import { useCanvasDimensions } from 'diagram/config';
import Icon from 'ui/Icon';
import { Box, Flex, HStack } from 'ui/Layout';
import { Text } from 'ui/Typography';

import { useSankeyExcelBindingContext } from '../../contexts/sankeyExcelBindingContext';
import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import { useSankeyInteractionContext } from '../../contexts/sankeyInteractionContext';
import CreatedFromExcelDataIcon from '../CreatedFromExcelDataIcon';
import { useIsDataFromExcel } from '../DataEditor/utils';
import DataSourceBindingAddress from '../DataSourceBindingAddress';
import SankeyFooterInfo from './SankeyFooterInfo';

type Props = {
  svgSize: { width: number; height: number };
};

const SankeyFooter: React.FC<Props> = ({ svgSize }) => {
  const { form } = useSankeyFormContext();
  const { hintText } = useSankeyInteractionContext();
  const { hasExcelBindingInThisSheet } = useSankeyExcelBindingContext();
  const isDataFromExcel = useIsDataFromExcel();

  const showCanvasResolution = form.watch('show_canvas_resolution') && svgSize;
  const showInfoBoxes = showCanvasResolution || isDataFromExcel;
  // Arrange elements vertically when SVGs are displayed small or when canvas resolution info is shown
  const showVerticalView =
    (svgSize && svgSize.width < 650) || showCanvasResolution;

  const canvas = useCanvasDimensions();

  return (
    <Box
      bg="gray.50"
      borderTop="1px solid"
      borderColor="gray.200"
      px={3}
      py={2}
    >
      <Flex
        alignItems="center"
        flexDirection={showVerticalView ? 'column' : 'row'}
        justifyContent="space-between"
        gap={2}
      >
        <Text color="gray.600" fontSize="sm" textAlign="center">
          <Icon color="gray.500" icon="comment-regular" mr={2} />
          {hintText || 'Click on any element to edit it'}
        </Text>
        {showInfoBoxes && (
          <HStack
            color="gray.500"
            flexWrap="wrap"
            fontSize="xs"
            justifyContent="center"
          >
            {showCanvasResolution && (
              <>
                <SankeyFooterInfo
                  label="Original"
                  value={`${canvas.width}x${canvas.height}px`}
                />
                <SankeyFooterInfo
                  label="Displayed"
                  value={`${Math.round(svgSize.width)}x${Math.round(
                    svgSize.height
                  )}px`}
                />
              </>
            )}
            {isDataFromExcel && (
              <SankeyFooterInfo
                label="Data source"
                value={
                  hasExcelBindingInThisSheet ? (
                    <DataSourceBindingAddress />
                  ) : (
                    <CreatedFromExcelDataIcon />
                  )
                }
              />
            )}
          </HStack>
        )}
      </Flex>
    </Box>
  );
};

export default SankeyFooter;
